import { useAuth } from "@clerk/clerk-react";
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  Grid,
  Link,
  Skeleton,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "schemas/routes";

import StyledSwitch from "components/Styled/Switch";
import StyledVaPage from "components/Styled/VAPage";
import QuestionBox from "components/Styled/VAPage/QuestionBox";
import GoogleConnect from "features/Settings/GoogleConnect";
import { fetcherAuth } from "utils/api";
import { useTracking } from "utils/useTracking";

const GMAIL_PERMISSIONS = [
  {
    icon: "fa-paper-plane",
    text: "Send email on your behalf",
    subtext: "Send personalized pitches directly from your email address",
  },
  {
    icon: "fa-envelope-open-text",
    text: "Read and organize your messages",
    subtext: "Track responses and schedule smart follow-ups automatically",
  },
  {
    icon: "fa-users",
    text: "Import your past partnerships",
    subtext:
      "We'll scan your contacts to find brands you've already worked with, so we never pitch them again. This keeps your relationships positive and professional.",
  },
];

interface Props {
  finishSetUpLater: () => void;
}

const SelfServeVaConnectGmail = ({ finishSetUpLater }: Props) => {
  const { getToken } = useAuth();
  const { setErrorAlert, setAlert } = useContext(AlertContext);

  const { emailHealth, fetchIntegrationHealth, fetchIntegrationLoading } =
    useContext(UserIntegrationsContext);
  const { currentOrg, setCurrentOrg } = useContext(OrganizationUserContext);

  const navigate = useNavigate();
  const { trackVaStep } = useTracking();

  const [loading, setLoading] = useState(false);
  const [importBrandCount, setImportBrandCount] = useState<string | null>(null);

  const isConnected = Boolean(emailHealth);

  const handleSubmit = async () => {
    if (!isConnected) {
      setAlert("Please click connect to google", "error");
      return;
    }
    await saveAndContinue();
  };

  const getNumberOfContacts = async () => {
    if (process.env.NODE_ENV === "development") {
      setImportBrandCount("6,500");
      return;
    }
    try {
      const { count } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-brands/import-contacts/total`,
        "GET",
      );
      setImportBrandCount((Math.round(count / 100) * 100).toLocaleString());
    } catch {
      setImportBrandCount("6,500");
    }
  };

  const saveAndContinue = async () => {
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/va-settings`,
        "PUT",
        {},
        {
          allowImportEmail: true,
          allowShareContacts: currentOrg?.allowShareContacts,
        },
      );
      navigate(`/${routes.reviewSelfServeOnboarding}`);
      trackVaStep(routes.reviewSelfServeOnboarding);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      getNumberOfContacts();
    }
    if (currentOrg?.id && !emailHealth) {
      fetchIntegrationHealth(currentOrg?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, emailHealth]);

  return (
    <StyledVaPage
      stepNumber={6}
      title="Connect Your Gmail"
      time="1"
      description="Connect your email to start sending personalized pitches to brands"
      body={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Required Permissions
          </Typography>
          <QuestionBox
            isActive={!Boolean(emailHealth)}
            styles={{ maxWidth: 600 }}
          >
            <Grid item xs={12} sx={{ mb: 3 }}>
              {GMAIL_PERMISSIONS.map((item) => (
                <Box
                  key={item.text}
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <Box
                    component="i"
                    className={`fa-regular ${item.icon}`}
                    sx={{
                      mr: 2,
                      fontSize: 18,
                      color: "purple.dark",
                      width: 24,
                      textAlign: "center",
                    }}
                  />

                  <Box>
                    <Typography sx={{ fontSize: { xs: 14, md: 16 } }}>
                      {item.text}
                    </Typography>

                    <Typography variant="caption" color="textSecondary">
                      {item.subtext}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>

            {isConnected ? (
              <Alert
                severity="success"
                sx={{
                  mb: 0,
                  fontSize: 14,
                  backgroundColor: "#E3FCE3",
                  color: "success.main",
                }}
              >
                Gmail connected successfully.
              </Alert>
            ) : (
              <GoogleConnect fullWidth />
            )}
          </QuestionBox>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Enhance your experience{" "}
            <Box component="span" sx={{ fontSize: 12, color: "purple.dark" }}>
              (optional)
            </Box>
          </Typography>
          <QuestionBox styles={{ maxWidth: 600 }}>
            <FormControlLabel
              sx={{ mb: 2 }}
              control={
                <StyledSwitch
                  checked={currentOrg?.allowShareContacts}
                  onChange={(e) => {
                    setCurrentOrg((prev) =>
                      prev
                        ? { ...prev, allowShareContacts: e.target.checked }
                        : prev,
                    );
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: 14, md: 18 }, ml: 1 }}>
                  Join the creator network for more brand opportunities
                </Typography>
              }
            />

            <Typography>
              Share your brand contacts anonymously to unlock{" "}
              <strong>
                {<>{importBrandCount}+</> || <Skeleton sx={{ width: 100 }} />}{" "}
              </strong>
              from other creators in Bento's network. Your contacts won't be
              link to you or reveal any partnership details.
            </Typography>

            <Grid
              container
              justifyContent="flex-start"
              alignItems="center"
              sx={{ mt: 2 }}
              gap={1}
            >
              <Chip
                size="small"
                label={
                  <>
                    <Box
                      component="i"
                      className="fa-solid fa-key"
                      sx={{ mr: 0.5 }}
                    />
                    Fully anonymous
                  </>
                }
                sx={{
                  fontSize: 12,
                  backgroundColor: "white",
                  color: "purple.dark",
                  border: `1px solid`,
                  borderColor: "purple.dark",
                  width: "fit-content",
                }}
              />
              {importBrandCount ? (
                <Chip
                  size="small"
                  label={
                    <>
                      <Box
                        component="i"
                        className="fa-solid fa-database"
                        sx={{ mr: 0.5 }}
                      />
                      Access to {importBrandCount}+ brands
                    </>
                  }
                  sx={{
                    fontSize: 12,
                    backgroundColor: "white",
                    color: "purple.dark",
                    border: `1px solid`,
                    borderColor: "purple.dark",

                    width: "fit-content",
                  }}
                />
              ) : (
                <></>
              )}
            </Grid>
          </QuestionBox>

          <Box>
            🔒 All data is encrypted and secure. We never share your personal
            information. Read our{" "}
            <Link
              sx={{ textDecoration: "none", color: "purple.dark" }}
              href="https://storage.googleapis.com/hatchways-crm.appspot.com/app-assets/Bento's%20Import%20Contacts%20Agreement-%20June%202025.pdf"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      }
      onBack={() => {
        trackVaStep(routes.emailWarmupSelfServeOnboarding);
        navigate(`/${routes.emailWarmupSelfServeOnboarding}`);
      }}
      buttonBack={"Back"}
      buttonNext={"Continue to Review"}
      onNext={handleSubmit}
      onFinishSetUpLater={finishSetUpLater}
      loading={loading || fetchIntegrationLoading}
      disabledNext={!isConnected}
    />
  );
};

export default SelfServeVaConnectGmail;
